import React from 'react';
import { useIntl } from "react-intl";
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/styles';
import HelpIcon from '@material-ui/icons/Help';
import PropTypes from "prop-types";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { getI18n } from "../../../../utils/intl";
import styles from './styles';
import { FormRadioGroup, FormSelect, RadioGroupSection } from '../../../../components';
import { get } from 'lodash';

const USAGE_OPTION_OPTIONAL = 'OPTIONAL';
const USAGE_OPTION_AT_LEAST_ONE_OPTION = 'ANY';
const USAGE_OPTION_ALL_TEMPLATES = 'ALL';

const CATEGORY_POSITION_FIXED = 'FIXED';
const CATEGORY_POSITION_FLEXIBLE = 'FLEXIBLE';

const CATEGORY_ACCESS_TYPE_GLOBAL = 'GLOBAL'
const CATEGORY_ACCESS_TYPE_ORG = 'ORG';

export const TCManagement = (params) => {
  const { classes, formObject, updateFormObject } = params;
  const {
    allowNonCompliantApisToBeUpdated,
    enforceTemplateOrdering,
    templateUsageRestriction = USAGE_OPTION_OPTIONAL,
    flexible,
    accessType,
  } = formObject;
  const intl = getI18n(useIntl());

  const handleUsageChange = (evt) => {
    if (evt.target.checked) {
      updateFormObject({
        ...formObject,
        templateUsageRestriction: USAGE_OPTION_AT_LEAST_ONE_OPTION,
      })
    } else {
      updateFormObject({
        ...formObject,
        templateUsageRestriction: USAGE_OPTION_OPTIONAL,
      })
    }
  };

  const handlePositionChange = (value) => {
    updateFormObject({
      ...formObject,
      flexible: (value === CATEGORY_POSITION_FLEXIBLE),
    })
  };

  const handleCriteriaChange = (field, value) => {
    updateFormObject({
      ...formObject,
      criteria: {
        ...formObject.criteria,
        [field]: value,
      },
    });
  }

  const handleUsageRequirementTypeChange = (value) => {
    updateFormObject({
      ...formObject,
      templateUsageRestriction: value,
    })
  };

  const handleOrderToggle = (evt) => {
    updateFormObject({
      ...formObject,
      enforceTemplateOrdering: !evt.target.checked,
    });
  };

  const handleEnforceRulesToggle = (evt) => {
    updateFormObject({
      ...formObject,
      allowNonCompliantApisToBeUpdated: !evt.target.checked,
    });
  };
  
  const handleOrgRestictionChange = (evt) => {
    updateFormObject({
      ...formObject,
      accessType: evt.target.checked ?
                CATEGORY_ACCESS_TYPE_ORG : CATEGORY_ACCESS_TYPE_GLOBAL,
    });
  };

  const API_ACCESS_FILTER = [
    { uuid: 'ANY',
      name: intl.getI18nMessage('label.category.details.management.applicability.apiaccess.any'),
    }, {
      uuid: 'PUBLIC',
      name: intl.getI18nMessage('label.category.details.management.applicability.apiaccess.public'),
    }, {
      uuid: 'PRIVATE',
      name: intl.getI18nMessage('label.category.details.management.applicability.apiaccess.private'),
    },
  ];

  const API_TYPE_FILTER = [
    { uuid: 'ANY',
      name: intl.getI18nMessage('label.category.details.management.applicability.apitype.any'),
    }, {
      uuid: 'SOAP',
      name: intl.getI18nMessage('label.category.details.management.applicability.apitype.soap'),
    }, {
      uuid: 'REST',
      name: intl.getI18nMessage('label.category.details.management.applicability.apitype.rest'),
    },
  ];
  
  const getCategoryPositionType = () => ([
    {
      id: CATEGORY_POSITION_FIXED,
      name: intl.getI18nMessage('label.category.details.management.title.category.position.fixed'),
      description: intl.getI18nMessage('label.category.details.management.title.category.position.fixed.helptext'),
    },
    {
      id: CATEGORY_POSITION_FLEXIBLE,
      name: intl.getI18nMessage('label.category.details.management.title.category.position.flexible'),
      description: intl.getI18nMessage('label.category.details.management.title.category.position.flexible.helptext'),
    },
  ]);

  const tooltipIconForOrdering = () => (
    <div className={classes.helpIconContainer}>
      <div>
        {intl.getI18nMessage('label.category.details.management.can.reorder.fixed.templates')}
      </div>
      <Tooltip classes={classes}
          title={intl.getI18nMessage('label.category.details.management.can.reorder.fixed.templates.tooltip')}
          arrow placement="right">
        <HelpIcon className={classes.helpIcon}/>
      </Tooltip>
    </div>
  );
  const requiredCategory = templateUsageRestriction !== USAGE_OPTION_OPTIONAL;
  const usageOptions = [{
      id: USAGE_OPTION_AT_LEAST_ONE_OPTION,
      name: intl.getI18nMessage('label.category.details.management.usage.option.1'),
    }, {
      id: USAGE_OPTION_ALL_TEMPLATES,
      name: intl.getI18nMessage('label.category.details.management.usage.option.2'),
    }];

  return (
    <div
      className={classes.managementContainer}
      data-apim-test={'category-management'}
    >
      <Typography variant="h4">
        {intl.getI18nMessage("label.category.details.management.title.category.applicability")}
      </Typography>
      <div className={classes.categoryRolesHelpText}>
        {intl.getI18nMessage('label.category.details.management.category.applicability.helptext')}
      </div>
      <Divider className={classes.detailDivider} />
      <FormSelect
        fieldContainerClass={classes.formSelectContainerClass}
        selectFieldClass={classes.selectFieldClass}
        labelClass={classes.labelClass}
        id={'category-criteria-api-type'}
        name={intl.getI18nMessage("label.category.details.management.applicability.apitype")}
        value={get(formObject, 'criteria.apiType', 'ANY')}
        data={API_TYPE_FILTER}
        handleChange={(value) => handleCriteriaChange('apiType', value)}
        noNativeSelect
        noNoneOption
      />
      <Divider className={classes.detailDivider} />
      <FormSelect
        fieldContainerClass={classes.formSelectContainerClass}
        selectFieldClass={classes.selectFieldClass}
        labelClass={classes.labelClass}
        id={'category-criteria-api-access'}
        name={intl.getI18nMessage("label.category.details.management.applicability.apiaccess")}
        value={get(formObject, 'criteria.apiAccess', 'ANY')}
        data={API_ACCESS_FILTER}
        handleChange={(value) => handleCriteriaChange('apiAccess', value)}
        noNativeSelect
        noNoneOption
      />
      <Divider className={classes.detailDivider} />
      <div className={classes.enforceRulesContainer}>
        <Typography variant="h4">
          {intl.getI18nMessage("label.category.details.management.restrict.by.org")}
        </Typography>
        <Switch
          id={'is-org-type-category'}
          data-apim-test={'is-org-type-category'}
          checked={accessType === CATEGORY_ACCESS_TYPE_ORG}
          onChange={handleOrgRestictionChange}
        />
      </div>
      <Divider />
      <div className={classes.categoryRolesHelpText}>
        {intl.getI18nMessage('label.category.details.management.restrict.by.org.helptext')}
      </div>
      
      <div className={classes.typeContainer}>
        <Typography variant="h4">
          {intl.getI18nMessage("label.category.details.management.title.category.position")}
        </Typography>
        <RadioGroupSection
          selected={flexible? CATEGORY_POSITION_FLEXIBLE : CATEGORY_POSITION_FIXED}
          onChange={(event) => handlePositionChange(event.target.value)}
          data={getCategoryPositionType()}
          id="type-radio-group"
        />
      </div>
      <Typography variant="h4">
        {intl.getI18nMessage("label.category.details.management.title.template.usage")}
      </Typography>
      <FormControlLabel
        control = {
          <Checkbox
            data-apim-test={'usage-requirements-check-box'}
            name={'requiredCategory'}
            onChange={handleUsageChange}
            checked={requiredCategory}
          />
        }
        label={intl.getI18nMessage('label.category.details.management.usage.enable')}
      />
      <div
        className={classes.usageGroup}
        id="template-usage-group"
      >
        <FormRadioGroup
          data={usageOptions}
          disabled={!requiredCategory}
          id="template-usage-restriction"
          name={''}
          onChange={handleUsageRequirementTypeChange}
          selected={templateUsageRestriction ?
            templateUsageRestriction : !!templateUsageRestriction}
          tooltip={''}
        />
      </div>
      <Typography variant="h4">
        {intl.getI18nMessage("label.category.details.management.edit.permission")}
      </Typography>
      <FormControlLabel
        control = {
          <Checkbox
            data-apim-test={'edit-order-check-box'}
            name={'canReorderTemplates'}
            onChange={handleOrderToggle}
            checked={!enforceTemplateOrdering}
          />
        }
        label={tooltipIconForOrdering()}
      />
      <br />
      <div className={classes.enforceRulesContainer}>
        <Typography variant="h4">
          {intl.getI18nMessage("label.category.details.management.enforce.role")}
        </Typography>
        <Switch
          id={'enforce-category-rules'}
          data-apim-test={'enforce-category-rules'}
          checked={!allowNonCompliantApisToBeUpdated}
          onChange={handleEnforceRulesToggle}
        />
      </div>
      <Divider />
      <div className={classes.categoryRolesHelpText}>
        {intl.getI18nMessage('label.category.details.management.enforce.role.helptext')}
      </div>
    </div>
  );
};
  
const mapStateToProps = () => ({
});

const mapDispatchToProps = {
};

TCManagement.propTypes = {
  classes: PropTypes.object,
  formObject: PropTypes.object,
  updateFormObject: PropTypes.func,
};

TCManagement.displayName = 'TCManagement';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(TCManagement);
